import {BehaviorSubject, Subject} from "rxjs";
import {inject, injectable} from "tsyringe";
import {AbstractLinkviewRepository} from "./abstract-linkview.repository";
import {BackendClient} from "../../connectivity/backend-client";
import {HttpError} from "../../connectivity/http-error";
import {CommandBus} from "../../services/command-bus.service";
import {EventBus} from "../../services/event-bus.service";
import {BulkCommand} from "../../commands/bulk.command";
import {CreateLinkviewCommand} from "../../commands/create-linkview.command";
import {RenameLinkviewCommand} from "../../commands/rename-linkview.command";
import {DeleteLinkviewCommand} from "../../commands/delete-linkview.command";
import {LockEntityCommand} from "../../commands/lock-entity.command";
import {ReleaseEntityLockCommand} from "../../commands/release-entity-lock.command";
import {OverrideEntityLockCommand} from "../../commands/override-entity-lock.command";
import {LOCKABLE_ENTITY_TYPE_LINKVIEW} from "../../commands/lockable-entity-type.constant";
import {LockConflictError} from "../../connectivity/lock-conflict.error";
import {appState} from "../../app.state";
import {deriveLocked} from "../helpers/derive-locked.helper";
import {ShortIdHelper} from "../../helpers/short-id.helper";
import {TransformerHelper} from "../../helpers/transformer.helper";
import {diffTiles} from "../link-tile-diff";
import {toDomain, type LinkTileRow} from "../link-tile-mapper";
import {DEFAULT_LINKVIEW_ICON, resolveLinkviewIcon} from "../helpers/linkview-icons.helper";
import type {Linkview} from "../entities/linkview.entity";
import type {LinkTile} from "../entities/link-tile.entity";

/** The board's key in the backend restore whitelist (`RestoreTypeMap.php:63`). */
const RESTORE_TYPE_LINKVIEW = `linkview`;

const QUERY_BOARDS = `/api/v1/query/linkview`;
const QUERY_TILES = `/api/v1/query/link-tile`;
const COMMAND = `/api/v1/command`;

/** One board as the query endpoint returns it. `lockedBy` is the TRANSPORT
 *  spelling — the serializer converts on the way out and the client never
 *  converts, in either direction. */
interface LinkviewRow {
    readonly uid: string;
    readonly title: string;
    /** Absent from an older server, `null` for a board created before the field existed. */
    readonly icon?: string | null;
    readonly lockedAt?: string | null;
    readonly lockedBy?: string | null;
}

/** Why a save failed, for a caller who only gets a boolean back. */
export type SaveFailure = 'conflict' | 'error';

interface QueuedSave {
    readonly linkviewUid: string;
    readonly tiles: LinkTile[];
    readonly resolve: (saved: boolean) => void;
}

/**
 * The backend implementation of the Linkview persistence contract: the boards
 * and their tiles live in the database rather than in one browser profile, so a
 * board survives a change of machine and is the same board for everybody.
 *
 * Anke's contract is implemented as agreed: `create`, `getTiles`, `saveTiles`,
 * `requestAddTile`, `items$` and `addTileRequested$` keep their declared
 * signatures unchanged, and her single `setLocked(uid, locked)` has been
 * replaced — with her — by `acquireLock` / `releaseLock` / `overrideLock`,
 * because a boolean can express neither a holder nor a take-over. Everything
 * this class adds beyond the contract (`refreshBaseline`, `lastSaveFailure`) is
 * additional, never a change to what she wrote.
 *
 * Three properties are what make this more than a set of HTTP calls:
 *
 * 1. **A save sends what changed, not the whole board.** The target state is
 *    compared against the baseline the client read when it entered edit mode, and
 *    the difference becomes create/update/delete/reorder commands inside ONE
 *    `BulkCommand`, which the server runs in one transaction.
 * 2. **Baseline and target are derived from ONE read.** Entering edit mode reads
 *    the tiles once; that single list becomes both the baseline and the list the
 *    board re-mounts from. Refreshing only the baseline would invert the
 *    protection: a take-over followed by a zero-edit save would then emit a
 *    delete for every tile the previous holder added.
 * 3. **Writes are serialised.** The contract explicitly does not guarantee that
 *    responses arrive in the order requests were sent. For localStorage that was
 *    harmless; against a backend it would turn "last write wins" into "last
 *    response wins", which is silent data loss. At most one save is in flight and
 *    the next one is diffed when it is DEQUEUED, against the baseline the
 *    previous save left behind.
 *
 * Registration: `@injectable()` with an explicit `@inject(...)` per constructor
 * parameter, and deliberately NOT `@singleton()`. `@singleton()` would register
 * the class under a second token as well, and two instances would mean two write
 * queues and two baselines drifting apart silently. Leaving `@injectable()` off
 * is not an option either — that only works for a parameterless constructor, and
 * this build emits no parameter metadata, so tsyringe would throw
 * `TypeInfo not known` at the first resolve.
 */
@injectable()
export class BackendLinkviewRepository extends AbstractLinkviewRepository {

    /**
     * Seeded EMPTY and filled asynchronously — unlike the localStorage
     * implementation, which could read its list synchronously in a field
     * initialiser. Every consumer already subscribes, so the initial empty
     * emission is safe.
     */
    public readonly items$ = new BehaviorSubject<Linkview[]>([]);

    public readonly addTileRequested$ = new Subject<string>();

    public readonly saveRequested$ = new Subject<string>();

    public readonly cancelRequested$ = new Subject<string>();

    /**
     * Why the last save failed, readable after `saveTiles` resolved `false`.
     * `saveTiles` keeps its boolean signature (Anke's contract), and a boolean
     * cannot say whether the lock was lost or the server broke — but the save
     * button has to tell the user two very different things.
     */
    public lastSaveFailure: SaveFailure | null = null;

    /** The state each board was in when the client entered edit mode. Never
     *  inherited from page load: comparing against a page-load state would write
     *  the previous holder's work back as delete and update commands. */
    private readonly _baselines = new Map<string, LinkTile[]>();

    /**
     * The edit-entry read, waiting to be picked up by the `getTiles` that
     * follows it. `acquireLock`/`overrideLock` return `Promise<void>`
     * and cannot hand a list back through their return value, so a component that
     * awaited the lock call and then called `getTiles` would issue a SECOND read —
     * and the board can change between two reads, which is the race this whole
     * mechanism exists to close. The entry is consumed once.
     */
    private readonly _editEntryCache = new Map<string, LinkTile[]>();

    /** Boards whose mounted target state is known to be stale, because a save of
     *  theirs was rejected with a lock conflict. A message telling the user to
     *  reload is advice; this is what actually stops the next click. */
    private readonly _staleTargets = new Set<string>();

    private readonly _queue: QueuedSave[] = [];

    private _draining = false;

    public constructor(
        @inject(BackendClient) private readonly _backendClient: BackendClient,
        @inject(CommandBus) private readonly _commandBus: CommandBus,
        @inject(EventBus) private readonly _eventBus: EventBus,
    ) {
        super();
        void this._loadBoards();

        // A RESTORED board has to reappear in the sidebar without a reload (O2D-PRD-101).
        //
        // Nothing else can do it: `command-send.listener`'s restore case republishes
        // `entity.changed` only for types in the entity register, and `linkview` is deliberately
        // not one — this repository IS the Linkview store, and it subscribes to no broker. So it
        // listens for the command itself, exactly the way `EntityManager` does for `create.`
        // (`entity.manager.ts:34`).
        //
        // COMMAND_SEND is dispatched only after the POST has resolved
        // (`command-bus.service.ts:158-168`), so the reload below reads the state the restore
        // produced, not the one before it. The pattern also fires on `success: false`, which
        // costs one harmless re-read of a list that did not change.
        //
        // Never unsubscribed, like the `appState` subscription below it: a container singleton
        // that lives as long as the application has no teardown point at which this would leak.
        this._eventBus.listenForCommandSendByPattern(/^restore\./).subscribe((event) => {
            if (event.payload?.command.mainEntityClass === RESTORE_TYPE_LINKVIEW) {
                void this._reloadBoards();
            }
        });

        // RE-DERIVE WHEN THE CURRENT USER CHANGES. The board list is fetched
        // ONCE, at construction, so it can easily be derived before hydration
        // has supplied the uid — and `deriveLocked` fails closed on a null uid.
        // Without this the holder's OWN board stays locked to them for the whole
        // session, which looks exactly like the lock being broken.
        //
        // Never unsubscribed on purpose: this repository is a container
        // singleton living for the lifetime of the application, so there is no
        // teardown point at which the subscription would be a leak.
        appState.listen$('user').subscribe(() => {
            this.items$.next(this.items$.value.map((item) => ({
                ...item,
                locked: deriveLocked(item.lockedBy, appState.user?.uid ?? null),
            })));
        });
    }

    /**
     * Creates a board. The command goes out FIRST and the board is only pushed
     * into `items$` once it succeeded — otherwise the sidebar would list a page
     * that does not exist on the server, and every tile saved onto it would fail
     * at parent resolution.
     *
     * A blank title throws rather than returning null, exactly as the
     * localStorage implementation does: the dialog makes it unreachable for a
     * user, so a blank title arriving here is a programming error.
     */
    public async create(title: string, icon: string | null = DEFAULT_LINKVIEW_ICON): Promise<Linkview> {
        const trimmed = title.trim();
        if (trimmed === '') {
            throw new Error('LinkviewRepository.create() requires a non-blank title');
        }

        const uid = ShortIdHelper.generate();
        const resolvedIcon = resolveLinkviewIcon(icon);
        const dispatched = await this._commandBus.dispatchCommand(new CreateLinkviewCommand({uid, title: trimmed, icon: resolvedIcon}));
        if (!dispatched) {
            throw new Error('LinkviewRepository.create() failed: the board was not created on the server');
        }

        // Born FREE: nobody holds a board that has just been created. `locked`
        // is derived from that, not asserted — with no holder it derives to view
        // mode for everybody, which is the same behaviour the old hardcoded
        // `locked: true` produced, but for a reason that stays true afterwards.
        const linkview = toLinkview({uid, title: trimmed, icon: resolvedIcon, lockedAt: null, lockedBy: null});
        this.items$.next([...this.items$.value, linkview]);
        return linkview;
    }

    /**
     * Renames a board. Same order as `create`: the command goes out FIRST and
     * `items$` only changes once the server accepted it — a title the server
     * refused (a board locked by somebody else) must never show in the sidebar.
     *
     * Only the title is replaced; icon and lock fields of the row stay as they
     * are. The rejection is the command bus's own error (an `HttpError`/
     * `LockConflictError` for a refused request), passed through so the caller
     * can show the reason.
     */
    public async rename(uid: string, title: string): Promise<Linkview> {
        const trimmed = title.trim();
        if (trimmed === '') {
            throw new Error('LinkviewRepository.rename() requires a non-blank title');
        }

        await this._runBoardCommand(new RenameLinkviewCommand({uid, title: trimmed}));

        // Read the row at emission time, not before the await: a lock change may
        // have been emitted while the command was in flight, and rebuilding from
        // the stale row would write the old holder back.
        const current = this.items$.value.find((item) => item.uid === uid);
        const renamed: Linkview = current
            ? {...current, title: trimmed}
            : toLinkview({uid, title: trimmed});
        if (current) {
            this.items$.next(this.items$.value.map((item) => (item.uid === uid ? renamed : item)));
        }
        return renamed;
    }

    /**
     * Deletes a board. Like `rename`, the entry leaves `items$` only after the
     * server accepted the command; the per-board client state (baseline, cached
     * edit-entry read, stale flag, queued saves) is dropped with it, so nothing
     * of a deleted board can be diffed or saved afterwards.
     */
    public async delete(uid: string): Promise<void> {
        await this._runBoardCommand(new DeleteLinkviewCommand({uid}));

        this._baselines.delete(uid);
        this._editEntryCache.delete(uid);
        this._staleTargets.delete(uid);
        this._discardQueued(uid);
        this.items$.next(this.items$.value.filter((item) => item.uid !== uid));
    }

    /**
     * Sends one board command (rename/delete) and turns a failure into a
     * rejection carrying the REAL cause. Read through `dispatchCommandForResult`
     * for the same reason `_runLockCommand` is: a bare boolean cannot tell "the
     * board is locked by somebody else" from "the server broke", and the user
     * has to be told which. On a lock conflict the rows are re-read BEFORE the
     * rejection settles, so `items$` already names the current holder when the
     * caller's catch runs.
     */
    private async _runBoardCommand(command: RenameLinkviewCommand | DeleteLinkviewCommand): Promise<void> {
        const result = await this._commandBus.dispatchCommandForResult(command);

        if (result.success) {
            return;
        }

        if (result.error instanceof LockConflictError) {
            await this._reloadBoards();
            throw result.error;
        }

        throw result.error instanceof Error
            ? result.error
            : new Error(`Linkview command ${command.name} failed for ${command.mainEntityUid}`);
    }

    /**
     * Takes the edit lock, and — because unlocking IS the edit-entry path —
     * performs the ONE read that re-derives baseline and target together.
     *
     * ORDERING IS LOAD-BEARING: the read is awaited and the cache filled BEFORE
     * `items$` emits. `items$` is a `BehaviorSubject`, so `.next()` notifies its
     * subscribers synchronously, and the board is one of them. Emitting first
     * would make the board's own re-read find an empty cache and issue a second
     * HTTP read, while every outcome still looked correct.
     */
    public async acquireLock(uid: string): Promise<void> {
        await this._runLockCommand(uid, new LockEntityCommand({
            entityType: LOCKABLE_ENTITY_TYPE_LINKVIEW,
            uid,
        }));

        await this.refreshBaseline(uid);
        this._emitLocalHolder(uid, appState.user?.uid ?? null);
    }

    /**
     * Leaving edit mode: reads nothing and re-mounts nothing, deliberately —
     * this is the control that keeps "one read on entry" from degenerating into
     * "a read on every lock change". The cached entry is dropped so no later
     * read can be served a stale list.
     */
    public async releaseLock(uid: string): Promise<void> {
        await this._runLockCommand(uid, new ReleaseEntityLockCommand({
            entityType: LOCKABLE_ENTITY_TYPE_LINKVIEW,
            uid,
        }));

        this._editEntryCache.delete(uid);
        this._emitLocalHolder(uid, null);
    }

    /**
     * Takes the lock over from somebody else. An edit-entry path like
     * `acquireLock`, so it performs the same single read — the previous holder
     * may well have written since this client last looked, and mounting a stale
     * list would ship their work back as deletes on the next save.
     *
     * `expectedHolderUid` goes out VERBATIM; see the command's own doc comment
     * for why converting it breaks every take-over.
     */
    public async overrideLock(uid: string, expectedHolderUid: string | null): Promise<void> {
        await this._runLockCommand(uid, new OverrideEntityLockCommand({
            entityType: LOCKABLE_ENTITY_TYPE_LINKVIEW,
            uid,
            expectedHolderUid,
        }));

        await this.refreshBaseline(uid);
        this._emitLocalHolder(uid, appState.user?.uid ?? null);
    }

    /**
     * Sends one lock command and turns its outcome into a resolution or the
     * right KIND of rejection. The two obligations of the lock contract both
     * live here, in one place, so the three methods above cannot diverge on
     * them:
     *
     * 1. THE OUTCOME IS READ THROUGH `dispatchCommandForResult`, not
     *    `dispatchCommand`. The latter resolves a bare boolean, and a boolean
     *    cannot distinguish "somebody else holds it, nothing was written" from
     *    "the server crashed" — the two need opposite responses from the user.
     *    When the payload carries a `LockConflictError`, THAT object is
     *    re-thrown rather than a new one, so the caller can compare identity.
     *
     * 2. THE ROW IS REFRESHED BEFORE THE REJECTION SETTLES. By the time the
     *    caller's catch runs, `items$` already carries the current `lockedBy` —
     *    which is what gives a second warning somebody to name. Refreshing
     *    afterwards would be too late: the catch would read the stale row and
     *    warn about the holder whose take-over was just refused.
     */
    private async _runLockCommand(uid: string, command: LockEntityCommand | ReleaseEntityLockCommand | OverrideEntityLockCommand): Promise<void> {
        const result = await this._commandBus.dispatchCommandForResult(command);

        if (result.success) {
            return;
        }

        if (result.error instanceof LockConflictError) {
            await this._reloadBoards();
            throw result.error;
        }

        throw result.error instanceof Error
            ? result.error
            : new Error(`Linkview lock command ${command.name} failed for ${uid}`);
    }

    /**
     * Writes the holder this client just became (or stopped being) into `items$`
     * without a further round-trip.
     *
     * The server is the authority and has already agreed — the command
     * succeeded — so re-reading the board list only to learn what was just
     * asked for would cost a request to arrive at the same answer. A conflict
     * takes the other path and DOES re-read, because there the server disagreed
     * and this client's belief is exactly what is wrong.
     */
    private _emitLocalHolder(uid: string, holderUid: string | null): void {
        this.items$.next(this.items$.value.map((item) => (item.uid === uid
            ? toLinkview({
                uid: item.uid,
                title: item.title,
                icon: item.icon,
                lockedAt: holderUid === null ? null : (item.lockedAt ?? new Date().toISOString()),
                lockedBy: holderUid,
            })
            : item)));
    }

    public requestAddTile(uid: string): void {
        this.addTileRequested$.next(uid);
    }

    public requestSave(uid: string): void {
        this.saveRequested$.next(uid);
    }

    public requestCancel(uid: string): void {
        this.cancelRequested$.next(uid);
    }

    /**
     * THE single edit-entry read: one request whose result becomes the baseline
     * AND the list the board re-mounts from. Returns that list so the caller can
     * hand it on, and clears any post-conflict refusal, because both sides have
     * just been re-derived together.
     */
    public async refreshBaseline(linkviewUid: string): Promise<LinkTile[]> {
        const tiles = await this._fetchTiles(linkviewUid);
        this._baselines.set(linkviewUid, tiles);
        this._editEntryCache.set(linkviewUid, tiles);
        this._staleTargets.delete(linkviewUid);
        return tiles;
    }

    /**
     * Serves the edit-entry read when there is one waiting, so entering edit mode
     * costs exactly one request rather than two. Otherwise it reads from the
     * server — the ordinary view-mode load.
     *
     * On a cache MISS, also backfills `_baselines` for a board the current
     * User already holds the lock for (review-fund, 2026-09-08, Anke's bug
     * report: "Refusing to save a Linkview board that was never read" —
     * confirmed live). This is reachable even though `acquireLock`/
     * `overrideLock` are the only other writers of `_baselines`: a page
     * reload (or a fresh mount of `<dashboard-creator>` for a board this
     * session never explicitly acquired) can find the board ALREADY locked
     * to this User — the server remembers the holder across reloads, but
     * `_baselines` is purely in-memory and does not survive one. Without
     * this, the board correctly renders unlocked/editable (`items$`/
     * `deriveLocked` are unaffected — they read straight from the server
     * row), the User can edit it, and the very next save is refused with no
     * way to recover except reloading (which reproduces the exact same
     * missing-baseline state).
     *
     * Deliberately NOT applied on the cache-HIT path above: that path
     * already means `acquireLock`/`overrideLock` populated the baseline
     * (and the cache) together, in this very session — re-deriving it here
     * too would just be a second, redundant read.
     *
     * KNOWN, ACCEPTED SIDE EFFECT (review-fund, 2026-09-08, Lauf 01, MINOR):
     * the lock is purely User-bound — `lockedBy` carries only the User's
     * uid, no session/tab identifier (see `LockEntityCommand`/
     * `_emitLocalHolder`) — so this backfill cannot distinguish "the SAME
     * User, a second tab/session that never itself called acquireLock" from
     * "a stale reload of the tab that DID". Before this fix, that second
     * tab/session could never actually save (refused with no baseline),
     * which incidentally also prevented two of the SAME User's own sessions
     * from racing a save against each other. This backfill lets that second
     * session now save too, each against its own independently-read
     * baseline — a lost-update risk BETWEEN a single User's own sessions,
     * not a new one introduced by this diff (the lock model has never had a
     * session/tab identifier), just one this fix newly makes reachable
     * instead of accidentally blocking. A real fix (a session/tab id in the
     * lock) is a larger change to the lock model, out of scope here.
     */
    public async getTiles(linkviewUid: string | undefined): Promise<LinkTile[]> {
        if (!linkviewUid) {
            return [];
        }

        const cached = this._editEntryCache.get(linkviewUid);
        if (cached) {
            this._editEntryCache.delete(linkviewUid);
            return cached;
        }

        const tiles = await this._fetchTiles(linkviewUid);

        const holderUid = this.items$.value.find((item) => item.uid === linkviewUid)?.lockedBy ?? null;
        if (holderUid !== null && holderUid === appState.user?.uid && !this._baselines.has(linkviewUid)) {
            this._baselines.set(linkviewUid, tiles);
        }

        return tiles;
    }

    /**
     * Never throws and resolves `false` on failure — the contract, unchanged.
     * The save is queued rather than sent straight away, so two clicks cannot
     * race each other into "last response wins".
     */
    public async saveTiles(linkviewUid: string | undefined, tiles: LinkTile[]): Promise<boolean> {
        if (!linkviewUid) {
            this.lastSaveFailure = 'error';
            return false;
        }

        if (this._staleTargets.has(linkviewUid)) {
            this.lastSaveFailure = 'conflict';
            return false;
        }

        return new Promise<boolean>((resolve) => {
            this._queue.push({linkviewUid, tiles: [...tiles], resolve});
            void this._drain();
        });
    }

    private async _loadBoards(): Promise<void> {
        try {
            const rows = await this._backendClient.post<LinkviewRow[]>(
                QUERY_BOARDS,
                false,
                {queryParams: []},
            );
            this.items$.next((rows ?? []).map((board) => toLinkview(board)));
        } catch (error) {
            console.error('Failed to load Linkviews', error);
        }
    }

    /** Re-reads the board list so `items$` carries the CURRENT holder. Used on a
     *  lock conflict, where this client's belief about who holds the lock is
     *  precisely what the server just contradicted. */
    private async _reloadBoards(): Promise<void> {
        await this._loadBoards();
    }

    /**
     * The filter key MUST be `linkview.uid`, not `linkview`: only a key ending in
     * `.uid` gets the join to the parent and the ShortId→UUID conversion. The
     * bare form matches nothing and reports no error — a board that silently
     * appears to have no tiles.
     */
    private async _fetchTiles(linkviewUid: string): Promise<LinkTile[]> {
        const rows = await this._backendClient.post<LinkTileRow[]>(
            QUERY_TILES,
            false,
            {queryParams: [{key: `linkview.uid`, comparator: `eq`, value: linkviewUid}]},
        );

        return (rows ?? []).map((row) => toDomain(row));
    }

    private async _drain(): Promise<void> {
        if (this._draining) {
            return;
        }

        this._draining = true;
        try {
            while (this._queue.length > 0) {
                const item = this._queue.shift()!;
                item.resolve(await this._runSave(item));
            }
        } finally {
            this._draining = false;
        }
    }

    /**
     * Runs one queued save. The diff is computed HERE, at dequeue time, not when
     * the save was enqueued: a save queued behind an in-flight one would
     * otherwise be diffed against a baseline its predecessor is about to replace,
     * and would ship deletes for tiles that by then exist — the original bug
     * wearing a queue.
     */
    private async _runSave(item: QueuedSave): Promise<boolean> {
        const {linkviewUid, tiles} = item;

        if (this._staleTargets.has(linkviewUid)) {
            this.lastSaveFailure = 'conflict';
            return false;
        }

        const baseline = this._baselines.get(linkviewUid);
        if (!baseline) {
            // Without a baseline the diff would read every existing tile as new
            // and emit an INSERT on an existing primary key for each of them, so
            // the whole save would fail on the server anyway — with a 500 instead
            // of an explanation. Refuse instead. Reaching this means edit mode was
            // never entered, which is the only path that reads the baseline.
            console.error('Refusing to save a Linkview board that was never read', linkviewUid);
            this.lastSaveFailure = 'error';
            return false;
        }

        const commands = diffTiles(linkviewUid, baseline, tiles);
        if (commands.length === 0) {
            this._baselines.set(linkviewUid, tiles);
            this.lastSaveFailure = null;
            return true;
        }

        try {
            // ONE call, on the wrapping bulk. `BulkCommandToDtoTransformer`
            // re-enters the transformer path per sub-command by itself, which is
            // what puts each command's own transformer on the path; calling it per
            // sub-command as well would transform everything twice.
            const body = await TransformerHelper.toDTO(new BulkCommand(commands));
            await this._backendClient.post<void>(COMMAND, false, body as unknown as Record<string, unknown>);

            this._baselines.set(linkviewUid, tiles);
            this.lastSaveFailure = null;
            return true;
        } catch (error) {
            if (error instanceof HttpError && error.status === 409) {
                return await this._onLockConflict(linkviewUid);
            }

            console.error('Failed to save Linkview tiles', error);
            this.lastSaveFailure = 'error';
            return false;
        }
    }

    /**
     * A rejected save PROVES somebody else has written, so both sides are
     * re-derived from one fresh read — and the board is then refused for saving
     * until an edit entry has re-mounted from that read. Between the two, the
     * baseline is fresh while the mounted target is not, which is exactly the
     * split that produces stale deletes.
     *
     * Whatever was queued behind it is discarded rather than replayed: those
     * command lists were computed for a state that no longer exists, and the user
     * has just been told nothing was saved.
     */
    private async _onLockConflict(linkviewUid: string): Promise<boolean> {
        await this.refreshBaseline(linkviewUid);
        this._staleTargets.add(linkviewUid);
        this._discardQueued(linkviewUid);
        this.lastSaveFailure = 'conflict';
        return false;
    }

    /** Every discarded save resolves `false` — never left unsettled, which would
     *  hang the caller awaiting it with no notification at all. */
    private _discardQueued(linkviewUid: string): void {
        for (let index = this._queue.length - 1; index >= 0; index--) {
            if (this._queue[index]!.linkviewUid === linkviewUid) {
                this._queue.splice(index, 1)[0]!.resolve(false);
            }
        }
    }

}

/**
 * One server row becomes one renderable board: the two lock fields pass through
 * untouched, and `locked` is DERIVED from them plus the asking user.
 *
 * This is the single derivation site for this implementation. Putting it in the
 * repository — rather than in the two components that render the flag — keeps
 * those components free of any knowledge of authentication, and gives the
 * "re-derive when the user changes" requirement one owner instead of two.
 *
 * A row from an older server that carries neither field coalesces to null on
 * both, which reads as FREE. That is the only reading that lets its owner ever
 * edit it again.
 */
function toLinkview(row: LinkviewRow): Linkview {
    const lockedBy = row.lockedBy ?? null;
    return {
        uid: row.uid,
        title: row.title,
        icon: row.icon ?? null,
        lockedAt: row.lockedAt ?? null,
        lockedBy,
        locked: deriveLocked(lockedBy, appState.user?.uid ?? null),
    };
}
